import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UserDatum } from '@app/shared/interfaces/files.interface';
import { InAppBrowser, InAppBrowserOptions } from '@awesome-cordova-plugins/in-app-browser/ngx';
import { Capacitor, HttpResponseType } from '@capacitor/core';
import { Http } from '@capacitor-community/http';
import { environment } from '@env';
import { LoadingController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../shared/shared.service';
import { ToastService } from '../toast/toast.service';

declare var google: any;
declare var gapi: any;
const inAppOptions: InAppBrowserOptions = {
  location: 'no',
  hidden: 'no',
  clearcache: 'no',
  clearsessioncache: 'no',
  zoom: 'no', //Android only
  hardwareback: 'no',
  mediaPlaybackRequiresUserAction: 'no',
  shouldPauseOnSuspend: 'no', //Android only
  closebuttoncolor: '#ffffff',
  disallowoverscroll: 'no', //iOS only
  toolbar: 'no', //iOS only
  enableViewportScale: 'no', //iOS only
  allowInlineMediaPlayback: 'no', //iOS only
  presentationstyle: 'fullscreen', //iOS only
  fullscreen: 'yes', //Windows only,
  hideurlbar: 'yes',
  usewkwebview: 'yes',
  toolbarcolor: '#a9a9a9',
  toolbartranslucent: 'no',
  toolbarposition: 'top',
  hidenavigationbuttons: 'yes',
  closebuttoncaption: 'X',
  lefttoright: 'no',
  hidespinner: 'yes',
};

@Injectable({
  providedIn: 'root',
})
export class DrivePickerService {
  accessToken!: string;
  picker: any;
  constructor(
    public iab: InAppBrowser,
    private sharedService: SharedService,
    private toastService: ToastService,
    private translateService: TranslateService,
    private loadingCtrl: LoadingController,
    private router: Router
  ) {}

  createPicker(accessToken) {
    this.sharedService.reset();
    this.accessToken = accessToken;
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      this.createPickerIOS();
    } else {
      this.createPickerWeb();
    }
  }

  pickerInit() {
    gapi.load('picker');
  }

  createPickerIOS() {
    const allowedMimeTypes = this.sharedService.getFileMimeTypes();
    let scriptString = `
      var s = document.createElement('script');
      s.setAttribute('src', 'https://apis.google.com/js/api.js');
      s.async = true;
      s.defer = true;
      s.onload = onApiLoad;
      document.body.appendChild(s);
      function onApiLoad() {
        if (window['gapi']) {
          gapi.load('picker', { 'callback': onPickerApiLoad });
        } else {
          console.error('gapi not loaded');
        }
      }
    
      function onPickerApiLoad() {
          var view = new google.picker.DocsView()
              .setIncludeFolders(false)
              .setSelectFolderEnabled(false)
              .setMimeTypes('${allowedMimeTypes}');
          var picker = new google.picker.PickerBuilder()
              .addView(view)
              .setOAuthToken('${this.accessToken}')
              .setDeveloperKey('${environment.GAPI_API_KEY}')
              .setCallback(pickerCallback)
              .enableFeature(google.picker.Feature.NAV_HIDDEN)
              .enableFeature(google.picker.Feature.FULLSCREEN)
              .setAppId('${environment.GAPI_APP_ID}')
              .build();
          picker.setVisible(true);

          setTimeout(() => {
            const pickerDialog = document.querySelector('.picker-dialog');
            const pickerDialogContent = document.querySelector('.picker-dialog .picker-dialog-content');

            if (pickerDialog) {
              pickerDialog.setAttribute(
                'style',
                \`
                width: 98vw !important;
                height: 95vh !important;
                max-width: 98vw !important;
                max-height: 95vh !important;
                top: 1vh !important;
                left: 1vw !important;
                border-radius: 12px !important;
                font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;
                \`
              );
            }

            if (pickerDialogContent) {
              pickerDialogContent.setAttribute(
                'style',
                \`
                width: 100% !important;
                height: 100% !important;
                font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;
                \`
              );
            }
          }, 350);

          var el = document.getElementsByClassName('picker-dialog-bg')[1];
          if(el){
            el.addEventListener('click', (event)=>{
              var message = JSON.stringify({event: 'close'});
              webkit.messageHandlers.cordova_iab.postMessage(message);
            })
          }
      }
    
      function pickerCallback(data) {
          var url = 'nothing';
          if (data.action == google.picker.Action.PICKED) {
              var doc = data.docs[0];
              url = doc.url;
              var data = {
                url: url, 
                fileId: doc.id, 
                fileName: doc.name, 
                mimeType: doc.mimeType, 
                size: doc.sizeBytes 
              }
              var message = JSON.stringify(data);
              webkit.messageHandlers.cordova_iab.postMessage(message);
          } else if (data.action == google.picker.Action.CANCEL) {
              var message = JSON.stringify({event: 'close'});
            webkit.messageHandlers.cordova_iab.postMessage(message);
          }
      }
    `;

    const browser = this.iab.create('https://mobile.eprintitsaas.org/blank', '', inAppOptions);

    browser.on('loadstop').subscribe(() => {
      browser.executeScript({ code: scriptString });
      browser.insertCSS({ file: '../../theme/style.css' });
    });

    browser.on('loaderror').subscribe((error) => {
      console.log('loaderror', error);
    });

    browser.on('message').subscribe((msg) => {
      if (msg?.data?.fileId) {
        const file: UserDatum = {
          id: msg.data.fileId,
          name: msg.data.fileName || '',
          mimeType: msg.data.mimeType || '',
          size: msg.data.size || 0,
        };
        this.downloadFile(file);
      }
      browser.close();
    });
  }

  createPickerWeb() {
    const allowedMimeTypes = this.sharedService.getFileMimeTypes();
    const view = new google.picker.DocsView()
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false)
      .setMimeTypes(allowedMimeTypes);
    this.picker = new google.picker.PickerBuilder()
      .enableFeature(google.picker.Feature.NAV_HIDDEN)
      // .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
      .setDeveloperKey(environment.GAPI_API_KEY)
      .setAppId(environment.GAPI_APP_ID)
      .setOAuthToken(this.accessToken)
      .addView(view)
      .setCallback(this.pickerCallback.bind(this))
      .build();
    this.picker.setVisible(true);

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
      setTimeout(() => {
        const pickerDialog = document.querySelector('.picker-dialog');
        const pickerDialogContent = document.querySelector('.picker-dialog .picker-dialog-content');

        if (pickerDialog) {
          pickerDialog.setAttribute(
            'style',
            `
                width: 98vw !important;
                height: 98vh !important;
                max-width: 98vw !important;
                max-height: 98vh !important;
                top: 1vh !important;
                left: 1vw !important;
                border-radius: 12px !important;
                font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;
                `
          );
        }

        if (pickerDialogContent) {
          pickerDialogContent.setAttribute(
            'style',
            `
                width: 100% !important;
                height: 100% !important;
                font-family: 'Google Sans', Arial, Helvetica, sans-serif !important;
                `
          );
        }
      }, 350);
    }

    const el = document.getElementsByClassName('picker-dialog-bg')[1];
    if (el) {
      el.addEventListener('click', () => {
        this.disposePicker();
      });
    }
  }

  disposePicker() {
    if (this.picker) {
      this.picker.dispose();
      this.picker = null;
    }
  }

  async pickerCallback(data: any) {
    if (data.action === google.picker.Action.PICKED) {
      const document: any = data[google.picker.Response.DOCUMENTS][0];
      if (document) {
        const file: UserDatum = {
          id: document.id,
          name: document.name,
          mimeType: document.mimeType,
          size: document.sizeBytes,
        };
        this.downloadFile(file);
      }
    } else if (data.action == google.picker.Action.CANCEL) {
      this.disposePicker();
    }
  }

  async downloadFile(file: UserDatum) {
    if (!this.sharedService.isFileSupportedWithType(file.name, file.mimeType)) {
      this.toastService.showErrorToast('FILE_TYPE_NOT_SUPPORTED');
      return;
    }

    const loading = await this.loadingCtrl.create({
      message: this.translateService.instant('DOWNLOADING_FILES'),
    });

    loading.present();

    // const url =
    // 'https://www.googleapis.com/drive/v3/files/' + fileId + '/export&mimeType=' + mimeType;
    const url = 'https://www.googleapis.com/drive/v3/files/' + file.id + '?alt=media';
    const respType: HttpResponseType = 'blob';
    const options = {
      url,
      headers: { Authorization: 'Bearer ' + this.accessToken },
      responseType: respType,
      params: {},
    };
    try {
      const response = await Http.get(options);

      if (response?.data) {
        await this.sharedService.addBlobToQueue(
          {
            content: response.data,
            contentType: file.mimeType,
            extension: this.sharedService.getExtension(file.name),
            fileName: file.name,
            lastModified: '',
            size: Number(file.size),
          },
          response.data
        );
      }
      loading.dismiss();
      if (!this.sharedService.isDomainReady()) {
        this.router.navigate(['/app/add-files/maps'], {
          state: { showMenu: false },
        });
      } else {
        this.router.navigate(['app/add-files/upload']);
      }
    } catch (error) {
      // const errorMsg = error.error.message
      this.toastService.showErrorToast('FILE_TYPE_NOT_SUPPORTED');
      console.log('api call error ===>', error);
    } finally {
      this.disposePicker();
      loading.dismiss();
    }
  }
}
